/* eslint-disable no-console */
import { Mixpanel } from 'common/utils/mixpanel';

/**
 * Which paywall did this person get?
 *
 * RevenueCat decides the offering (and therefore the trial length) per
 * customer - today that is the 14 vs 30 day trial experiment, tomorrow it
 * could be anything else set up in the dashboard. RevenueCat records the
 * choice against the customer on its side, but nothing else learns about it
 * unless the app says so. This module is the one place that reads the
 * answer off the offering and stamps it on our own analytics:
 *
 *  - Mixpanel super properties, so every later event carries it
 *  - the Mixpanel person, so a user can be looked up by variant
 *  - the pre-purchase Adjust events (callers pass the props through), so
 *    ad-campaign reports can be split by variant
 *
 * `trial_days` comes from the offering's Metadata tab in the RevenueCat
 * dashboard (`{"trial_days": 14}`), so the app never has to parse offering
 * names. Offerings without it just report their identifier.
 */

export interface TrialVariantProps {
  offering?: string;
  trial_days?: number;
}

interface OfferingLike {
  identifier?: string;
  metadata?: Record<string, unknown> | null;
}

let current: TrialVariantProps = {};

const parseTrialDays = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

export const describeOffering = (offering: OfferingLike | null | undefined): TrialVariantProps => {
  const props: TrialVariantProps = {};
  if (offering?.identifier) props.offering = offering.identifier;
  const days = parseTrialDays(offering?.metadata?.trial_days);
  if (days !== undefined) props.trial_days = days;
  return props;
};

/**
 * Record the offering the user is about to be shown. Safe to call as often
 * as offerings are fetched; never throws into product code.
 */
export const rememberTrialVariant = (offering: OfferingLike | null | undefined): TrialVariantProps => {
  current = describeOffering(offering);
  if (!current.offering) return current;
  try {
    Mixpanel.register(current);
    Mixpanel.people.set({
      trial_variant: current.offering,
      ...(current.trial_days !== undefined ? { trial_days: current.trial_days } : {}),
    });
  } catch (e) {
    console.warn('[paywall] trial variant tracking failed', e);
  }
  return current;
};

/** The last remembered variant, for events fired after the offering was fetched. */
export const getTrialVariant = (): TrialVariantProps => current;

/** Adjust callback parameters must be strings. */
export const trialVariantForAdjust = (props: TrialVariantProps = current): Record<string, string | undefined> => ({
  offering: props.offering,
  trial_days: props.trial_days !== undefined ? String(props.trial_days) : undefined,
});

// Offline, or before RevenueCat is configured for a guest, getOfferings can
// hang. Callers wrap it in this and treat null as "not known".
export const OFFERINGS_TIMEOUT_MS = 4000;

export const withTimeout = <T, >(promise: Promise<T>, ms: number): Promise<T | null> => new Promise((resolve) => {
  const timer = setTimeout(() => {
    resolve(null);
  }, ms);
  promise.then(
    (value) => { clearTimeout(timer); resolve(value); },
    () => { clearTimeout(timer); resolve(null); },
  );
});

interface PeriodLike {
  number?: number;
  unit?: string;
}

/**
 * Trial length in days from a purchases-js trial period, only when the unit
 * converts to whole days without guessing (days and weeks). Months and
 * years return undefined so callers fall back to wording with no number.
 */
export const trialDaysFromPeriod = (period: PeriodLike | null | undefined): number | undefined => {
  if (!period || typeof period.number !== 'number' || !Number.isFinite(period.number) || period.number <= 0) {
    return undefined;
  }
  if (period.unit === 'day') return period.number;
  if (period.unit === 'week') return period.number * 7;
  return undefined;
};
